import React, { useState, useRef } from "react";
import "./contentpage.css";
import QuoteCard from "../components/QuoteCard";
import Question from "../components/Question";
import FloatingPageNav from "../components/FloatingPageNav";
import LandingSection from "./LandingSection";
import BadgePopup from "../components/Badgepopup";

function Economics26() {
  const [isOpen, setIsOpen] = useState(true);
  const contentRef = useRef(null);
  const links = {
    prev: "/islamic-trade-page-25",
    index: "/index-economics",
    next: "/economics-27",
  };
  const titleText = "Economics: Choices, Scarcity, and Purpose ";
  const questions = [
    {
      text: "1. According to the Classical and Austrian economic perspective described in the passage, why do methods of the natural sciences often fail when applied to economics?",
      options: [
        {
          value: "A",
          label: "Because economic data is too limited to form accurate models",
        },
        {
          value: "B",
          label:
            "B. Because economists reject all forms of theory and abstraction",
        },
        {
          value: "C",
          label:
            "C. Because human beings are purposeful actors with changing goals and preferences, not mechanical variables",
        },
        {
          value: "D",
          label:
            "D. Because economics is only concerned with historical events",
        },
      ],
      correct: "C",
    },
    {
      text: "2. What key concept distinguishes economics from natural sciences like physics?",
      options: [
        { value: "A", label: "A. Use of mathematics" },
        { value: "B", label: "B. Controlled experiments" },
        { value: "C", label: "C. Study of rational particles" },
        {
          value: "D",
          label: "D. Unpredictability of human behavior and purposeful action",
        },
      ],
      correct: "D",
    },
    {
      text: "3. In the Austrian tradition, what does the term rational mean in the context of human action?",
      options: [
        {
          value: "A",
          label: "A. Emotionless and perfectly logical decision-making",
        },
        { value: "B", label: "B. Behavior based purely on economic gain" },
        {
          value: "C",
          label: "C. Action guided by deliberate reasoning toward a chosen end",
        },
        { value: "D", label: "D. Decisions made using scientific models" },
      ],
      correct: "C",
    },
    {
      text: "4. What is the main criticism Austrian economists have toward using mathematical models in economics?",
      options: [
        { value: "A", label: "A. They are too simple to understand" },
        { value: "B", label: "B. They are not visually appealing" },
        {
          value: "C",
          label:
            "C. They fail to capture the complexity of human action and decision-making",
        },
        { value: "D", label: "D. They do not use enough historical data" },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={26}
        timerMinutes={60}
        currentLevel={2}
        unlockedBadges={3}
      />
      <QuoteCard
        text="Many who are self-taught far excel the doctors, masters, and bachelors of the most renowned universities."
        author="Ludwig Von Mises"
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 26</b>! Today, we begin a journey into the heart of
        economics, not merely as a study of graphs, prices, or policies, but as
        a profound exploration of human choice in a world of scarcity. Far from
        being a sterile or purely mathematical science, economics, as seen
        through the Classical and Austrian lens, is a reflection of our values,
        our priorities, and the purposeful actions we take to improve our lives.
        By understanding economics as the study of human action, we lay the
        foundation for a richer, more thoughtful view of markets, society, and
        our moral responsibilities within them.
        <br />
        <br />
        <b>What is Economics?</b>
        <br />
        <br /> Economics is often misunderstood. People hear the term and think
        of stock markets, money, inflation rates, or GDP. While these are all
        related to economics, they are not its essence. To truly understand what
        economics is, one must go beyond superficial definitions and explore its
        foundations, especially through the lens of Classical and Austrian
        economic traditions. This first page of our curriculum will lay the
        groundwork for a deeper appreciation of economics as the study of human
        action in the face of scarcity.
        <br />
        <br />
        <b>Economics vs. Natural Sciences</b>
        <br />
        <br />
        Unlike the natural sciences such as physics or mathematics, economics
        does not operate in an environment where most dependent variables can be
        held constant. Physicists study the behavior of atoms in a vacuum, under
        strictly controlled, adiabatic conditions. Mathematicians work with
        abstract constants, axioms, and formulas that yield predictable results.
        <br />
        <br />
        Economics, by contrast, deals with human beings i.e individuals with
        varying goals, preferences, resources, emotions, cultures, and time
        horizons. The “laboratory” of the economist is the dynamic,
        unpredictable world of human society. No economic experiment can
        perfectly isolate variables or eliminate external influences. The
        factors that influence economic behavior such as time preference,
        cultural values, or personal goals are constantly changing and
        interacting in complex ways. Thus, studying economics is fundamentally
        different from studying chemistry, physics, or engineering.
        <br />
        <br />
        This is why attempts to model human behavior with the same methods used
        in the hard sciences often fall short. The Austrian school of economics
        rightly critiques this positivist approach, arguing that human action
        cannot be captured in mechanical equations. People are not particles;
        they are purposeful actors.
        <br />
        <br />
        <b>The Austrian Definition: Human Action Under Scarcity</b>
        <br />
        <br />
        Austrian economists define economics as the study of human action under
        conditions of scarcity. This view is most clearly articulated by Ludwig
        von Mises in his seminal work, Human Action. Scarcity, in this context,
        refers to the fact that human wants are unlimited, while resources are
        finite. Time, money, labor, and raw materials all are limited. This
        scarcity necessitates choice.
        <br />
        Human action, then, is about choosing among competing ends. Since we
        cannot have everything we want simultaneously, we must prioritize. The
        act of choosing is itself the subject matter of economics.
        <br />
        Crucially, when Austrians speak of human action, they are referring to
        purposeful behavior. This is behavior undertaken with an end in mind. It
        is not reflexive or instinctive like blinking or sneezing. Nor is it
        impulsive, like yelling in anger. Human action is rational, in the
        specific sense that it is the product of deliberative thought, directed
        toward a goal.
        <br />
        <br />
        <b>Purposeful Behavior: The Core of Economic Action</b>
        <br />
        <br />
        The idea of purposeful behavior is what distinguishes economics from
        psychology or sociology. Where psychology might explore subconscious
        drives or emotional states, economics focuses on the deliberate choices
        individuals make to improve their circumstances.
        <br />
        Murray Rothbard, one of Mises’ most influential students, expands on
        this idea. He defines human action as{" "}
        <i>
          “purposeful behavior toward the attainment of ends in some future
          period which will involve the fulfillment of wants otherwise remaining
          unsatisfied.”
        </i>{" "}
        This definition is crucial for several reasons.
        <br />
        First, it places action in a temporal context: the actor aims to achieve
        something in the future. Second, it highlights that the actor perceives
        a state of dissatisfaction and acts to change that. Finally, it implies
        that action involves means,resources, time, tools that are employed to
        achieve ends.
        <br />
        It is also important to note Rothbard’s use of the word rational. In
        popular discourse, rationality often implies a cold, calculating mindset
        devoid of emotion. But in the Austrian tradition, rationality simply
        means that the action is the result of reasoning, no matter how flawed
        or misguided that reasoning may be. If a man gives money to a charity
        because he believes it will improve the world, that action is rational
        even if others disagree with his beliefs or values.
        <br />
        <br />
        <b>The Role of the Economist</b>
        <br />
        <br />
        Economists, then, are students of human behavior in the face of
        scarcity. They observe how individuals choose between different ends,
        how they allocate their limited resources, and how those choices produce
        patterns in society. But not all economists do this well.
        <br />
        Modern mainstream economics often falls into the trap of short-term
        thinking and isolated analysis. Many contemporary economists analyze
        only the immediate effects of a policy or action, and often only on a
        specific group. For example, a government subsidy might benefit farmers
        in the short run. A superficial analysis stops there and proclaims
        success.
        <br />
        But good economists, as Henry Hazlitt emphasizes in his classic book
        Economics in One Lesson, look further. They examine not only the
        immediate effects but also the long-term consequences. They consider not
        only the impact on one group, but also the ripple effects on others. A
        subsidy to farmers may lead to higher taxes, increased government debt,
        or distorted prices, which hurt consumers and producers elsewhere in the
        economy.
        <br />
        Hazlitt famously wrote:{" "}
        <i>
          “The art of economics consists in looking not merely at the immediate
          but at the longer effects of any act or policy; it consists in tracing
          the consequences of that policy not merely for one group but for all
          groups.”
        </i>
        <br />
        <br />
        <b>The Importance of Long-Term Thinking</b>
        <br />
        <br />
        The Austrian tradition is particularly strong in emphasizing the
        unintended consequences of economic policies. F.A. Hayek, another
        towering figure in this tradition, warned against the “pretense of
        knowledge” that comes from over-reliance on data and mathematical
        models. He cautioned that central planners could never possess the
        dispersed knowledge held by individuals acting in a free market.
        <br />
        This insight leads to an appreciation for the role of time and
        uncertainty in economic life. Every economic decision is made under
        uncertainty. Entrepreneurs, investors, and consumers must forecast the
        future, however imperfectly. This is why markets exist, to coordinate
        the dispersed plans of millions of individuals who each possess unique
        knowledge of their local circumstances.
        <br />A good economist, therefore, must cultivate humility. He must
        resist the temptation to treat people like variables in an equation. He
        must also take the long view, recognizing that policies which seem
        beneficial in the short term may sow the seeds of long-term harm.
        <br />
        <br />
        <b>Economics as a Moral Science</b>
        <br />
        <br />
        As it deals with human action, economics is also inextricably linked to
        ethics and morality. While the Austrian School avoids making moral
        judgments about ends, it acknowledges that human beings act with values
        in mind. Every choice implies a preference; every action reveals what
        the actor values more at that moment.
        <br />
        Economics, then, cannot be completely value-free. The Classical and
        Austrian traditions accept this. Some economists within this tradition
        argue that sound money encourages long-term thinking and moral
        responsibility. In their view, inflationary monetary systems distort
        economic signals and incentivize short-term consumption over long-term
        investment.
        <br />
        This reinforces the point that economics is about more than numbers,
        rather it is about human flourishing. How societies organize themselves
        economically has profound implications for freedom, prosperity, and
        culture.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        In today’s lesson, we explored the foundational question: What is
        economics? Moving beyond common associations like money or GDP, we
        examined economics as the study of human action in the face of scarcity.
        Drawing from the Austrian tradition, we understood that economics
        differs from the natural sciences because it deals with purposeful,
        value-driven human behavior rather than predictable physical reactions.
        We learned that every economic choice reflects priorities and
        trade-offs, as individuals act to achieve desired ends with limited
        means. The lesson also emphasized the importance of long-term thinking,
        the limits of mathematical modeling in capturing human behavior, and the
        moral dimension of economic decisions.
        <br />
        <br />
        <b>Coming up next</b>
        <br />
        <br />
        Now that we have laid the foundation by understanding economics as the
        study of purposeful human action under scarcity, we turn next to a
        powerful lesson in economic reasoning: the broken window fallacy. In
        tomorrow’s session, we will explore why destruction is not a path to
        prosperity, and how focusing only on what is immediately visible leads
        to flawed conclusions. Through the lens of opportunity cost, capital
        formation, and long-term consequences, we will examine why true economic
        growth is rooted not in activity alone, but in productivity and
        purposeful allocation of scarce resources.
        <br />
        <br />
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
      <BadgePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        level="Level 2"
        badgeImg="./achieve.png"
        heading="Free Market Novice"
        subtitle="Beginner - Principles of Austrian Economics"
        mainImg="./freemarketnovice.png"
        description="Awarded for embarking on the path of economic reason. You've established the foundational insight that economics begins with purposeful human action and individual choice."
      />
    </div>
  );
}

export default Economics26;
